import React from 'react';

interface JobProgressProps {
  /** 0-100. Values outside that range are clamped. */
  progressPercent: number;
  /** `downloading`, `processing` or `uploading`. */
  mode: string;
  /** Frame rate the file is encoding at. Hidden when 0 (or absent). */
  fps?: number;
}

const MODE_LABELS: Record<string, string> = {
  downloading: 'Downloading',
  processing: 'Processing',
  uploading: 'Uploading',
};

/** Mode name plus a linear progress bar, sized for a table cell. */
export default function JobProgress({ progressPercent, mode, fps }: JobProgressProps) {
  const raw = Number.isFinite(progressPercent) ? progressPercent : 0;
  const pct = Math.round(Math.min(100, Math.max(0, raw)));

  // Fall back to the raw string so an unrecognised mode still renders.
  const label = MODE_LABELS[mode] ?? mode;

  // The server sends 0 rather than null when a job isn't reporting a frame
  // rate, so there is nothing meaningful to show at zero.
  const showFps = typeof fps === 'number' && Number.isFinite(fps) && fps > 0;

  return (
    <div className="server-queues__progress">
      <div className="server-queues__progress-row">
        <span className="server-queues__progress-mode">{label}</span>
        <div
          className="server-queues__progress-track"
          role="progressbar"
          aria-label={`${label} progress`}
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="server-queues__progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="server-queues__progress-pct">{pct}%</span>
      </div>

      {/*
        Always rendered, even with nothing to say. The stylesheet gives it a
        fixed height, so a job that starts or stops reporting a frame rate
        between polls doesn't shift the bar above it.
      */}
      <span className="server-queues__progress-fps">{showFps ? `${fps} fps` : ''}</span>
    </div>
  );
}
